import React, { useContext } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import { useTranslation } from 'react-i18next';

import Table from 'Components/Table';
import { Text, Box } from 'Components/Base';
import ProgressBar from 'Components/ProgressBar';
import { COURSE_GRADE_STAT_TYPE, COURSE_GRADE_TYPE } from 'Domains/CourseGradeReportData';
import * as mixpanel from 'config/mixpanel';
import { SYLLABUS_MODE } from 'Domains/syllabus';
import { alignClassCoursesByCode } from 'Domains/utils';

import MasteryDistributionBar from './MasteryDistributionBar';

const TextWrapper = styled(Box)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center;
  font-weight: ${({ fontWeight }) => fontWeight ?? 'normal'};
`;

const AttendanceTableStyle = styled(Table)`
  & .MuiTableContainer-root {
    overflow-x: unset !important;
  }

  & .MuiTableCell-sizeSmall {
    padding: 5px 10px;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      &:nth-child(n + 2) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.white};
      }
    }
  }

  & .MuiTableRow-head:nth-child(2) {
    & .MuiTableCell-head {
      &:nth-child(n + 2) {
        display: table-cell;
        max-width: 0px;
      }
    }
  }

  & .MuiTableRow-root {
    & .MuiTableCell-root:nth-child(1).MuiTableCell-head {
      position: sticky;
      left: 0 !important;
      width: 100px;
      z-index: 3;
    }

    & .MuiTableCell-root:nth-child(1):not(.MuiTableCell-head) {
      position: sticky;
      left: 0 !important;
      width: 100px;
      z-index: 2;
    }
  }

  & .MuiTableRow-root:not(.MuiTableRow-head) {
    cursor: ${({ cursorPointer }) => (cursorPointer ? 'pointer' : 'auto')};
  }

  & .MuiTableRow-root:not(.MuiTableRow-head):nth-child(2n + 1) {
    & .MuiTableCell-root:nth-child(1) {
      background-color: rgb(247, 251, 253) !important;
    }
  }

  & .MuiTableRow-root:not(.MuiTableRow-head):nth-child(2n) {
    & .MuiTableCell-root:nth-child(1) {
      background-color: rgb(235, 245, 250) !important;
    }
  }

  & .MuiTableCell-root.MuiTableCell-body:empty {
    background-color: ${({ theme }) => theme.colors.greyscale.grey4};
  }

  td:nth-child(${props => props.columnHovering}) {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

const TKAttendanceTableStyle = styled(Table)`
  & .MuiTableCell-head {
    white-space: nowrap;
  }
  & .MuiTableContainer-root {
    overflow-x: unset !important;
  }

  & .MuiTableCell-sizeSmall {
    padding: 5px 10px;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      &:nth-child(n + 3) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.white};
      }
    }
  }

  & .MuiTableRow-head:nth-child(2) {
    & .MuiTableCell-head {
      &:nth-child(n + 3) {
        display: table-cell;
        max-width: 0px;
      }
      &:nth-child(-n + 2) {
        > div {
          position: relative;
          top: -18px;
        }
      }
    }
  }

  & .MuiTableRow-root {
    & .MuiTableCell-root:nth-child(1).MuiTableCell-head {
      position: sticky;
      left: 0 !important;
      width: 100px;
      z-index: 3;
    }

    & .MuiTableCell-root:nth-child(1):not(.MuiTableCell-head) {
      position: sticky;
      left: 0 !important;
      width: 100px;
      z-index: 2;
    }

    & .MuiTableCell-root:nth-child(2).MuiTableCell-head {
      position: sticky;
      left: 121px !important;
      width: 120px;
      z-index: 3;
    }

    & .MuiTableCell-root:nth-child(2):not(.MuiTableCell-head) {
      position: sticky;
      left: 121px !important;
      width: 120px;
      z-index: 2;
    }
  }

  & .MuiTableRow-root:not(.MuiTableRow-head) {
    cursor: ${({ cursorPointer }) => (cursorPointer ? 'pointer' : 'auto')};
  }

  & .MuiTableRow-root:not(.MuiTableRow-head):nth-child(2n + 1) {
    & .MuiTableCell-root:nth-child(1) {
      background-color: rgb(247, 251, 253) !important;
    }
    & .MuiTableCell-root:nth-child(2) {
      background-color: rgb(247, 251, 253) !important;
    }
  }

  & .MuiTableRow-root:not(.MuiTableRow-head):nth-child(2n) {
    & .MuiTableCell-root:nth-child(1) {
      background-color: rgb(235, 245, 250) !important;
    }
    & .MuiTableCell-root:nth-child(2) {
      background-color: rgb(235, 245, 250) !important;
    }
  }

  & .MuiTableCell-root.MuiTableCell-body:empty {
    background-color: ${({ theme }) => theme.colors.greyscale.grey4};
  }

  td:nth-child(${props => props.columnHovering}) {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

const TableHead = props => <Text fontWeight={600} {...props} />;
const HeaderColSpanText = styled(TableHead)`
  white-space: nowrap;
`;

export const CourseGradeTable = ({
  data: { schoolGrade, classes: rawClasses = [], courses = [], subject },
  maxHeight,
  type,
  setSyllabusId,
  myProfileId,
  statType,
  mode,
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const { t } = useTranslation('courseGrade');
  const [columnHovering, setColumnHovering] = React.useState(null);
  // Match each class's courses to the column headers by course code, not position.
  const classes = React.useMemo(() => alignClassCoursesByCode(rawClasses, courses), [
    rawClasses,
    courses,
  ]);
  const isGrowthRateType = type === COURSE_GRADE_TYPE.GROWTH_RATE;
  const isMastery = type === COURSE_GRADE_TYPE.MASTERY;

  const getTextColor = value => {
    if (value > 0) return theme.colors.passText;
    if (value < 0) return theme.colors.failText;
  };

  const columns = [
    {
      id: 'classNo',
      Header: <TableHead>{t('CLASS')}</TableHead>,
      accessor: properties => properties?.classNo,
      width: 100,
      Cell: ({ cell: { row } }) => {
        return (
          <TextWrapper width={'100px'}>
            {schoolGrade?.shortName}/{row?.original?.classNo}
          </TextWrapper>
        );
      },
    },
    {
      id: 'courseUnit',
      Header: (
        <HeaderColSpanText>{t('UNIT', { subjectName: subject?.name ?? '' })}</HeaderColSpanText>
      ),
      columns: courses.map((course, idx) => ({
        id: course.courseCode,
        Header: <TableHead>{course.courseName}</TableHead>,
        accessor: properties => properties.courses,
        width: 184,
        disableSortBy: true,
        Cell: ({ cell: { value } }) => {
          if (value[idx] && value[idx][type] != null) {
            if (isGrowthRateType) {
              const formatValue = `${_.round(value[idx][type])}%`;
              return (
                <TextWrapper color={getTextColor(value[idx][type])} fontWeight={'bold'}>
                  {value[idx][type] > 0 ? `+${formatValue}` : formatValue}
                </TextWrapper>
              );
            } else {
              return (
                <ProgressBar
                  progress={_.round(value[idx][type], 2)}
                  thresholdType={type === COURSE_GRADE_TYPE.EXAMINATION_AVERAGE_RESULT ? 2 : 1}
                />
              );
            }
          } else {
            return null;
          }
        },
      })),
    },
  ];

  const tkColumns = [
    {
      id: 'classNo',
      Header: <TableHead>{t('CLASS')}</TableHead>,
      accessor: properties => properties?.classNo,
      width: 100,
      Cell: ({ cell: { row } }) => {
        return (
          <TextWrapper width={'100px'}>
            {schoolGrade?.shortName}/{row?.original?.classNo}
          </TextWrapper>
        );
      },
    },
    {
      id: 'totalStudents',
      Header: <TableHead>{t('STUDENT_COUNT')}</TableHead>,
      accessor: properties => properties?.totalStudents,
      width: 100,
      Cell: ({ cell: { row } }) => {
        return <TextWrapper width={'100px'}>{row?.original?.totalStudents}</TextWrapper>;
      },
    },
    {
      id: 'courseUnit',
      Header: (
        <HeaderColSpanText>{t('UNIT', { subjectName: subject?.name ?? '' })}</HeaderColSpanText>
      ),
      columns: courses.map((course, idx) => ({
        id: course.courseCode,
        Header: <TableHead>{course.courseName}</TableHead>,
        accessor: properties => properties.courses,
        width: 184,
        disableSortBy: true,
        Cell: ({ cell: { value } }) => {
          if (value[idx] && value[idx][type] != null && !isMastery) {
            if (isGrowthRateType) {
              const formatValue = `${_.round(value[idx][type][statType])}%`;
              return (
                <TextWrapper color={getTextColor(value[idx][type][statType])} fontWeight={'bold'}>
                  {value[idx][type][statType] > 0 ? `+${formatValue}` : formatValue}
                </TextWrapper>
              );
            }
            return (
              <ProgressBar progress={_.round(value[idx][type][statType], 2)} thresholdType={1} />
            );
          } else if (
            isMastery &&
            value[idx] &&
            [1, 2, 3, 4].some(lv => value[idx][`masteryLV${lv}`] != null)
          ) {
            return (
              <MasteryDistributionBar
                lv1={value[idx].masteryLV1}
                lv2={value[idx].masteryLV2}
                lv3={value[idx].masteryLV3}
                lv4={value[idx].masteryLV4}
              />
            );
          } else {
            return null;
          }
        },
      })),
    },
  ];

  return mode === SYLLABUS_MODE.TRADITIONAL ? (
    <AttendanceTableStyle
      dense
      stickyHeader
      columns={columns}
      data={classes}
      style={{ maxHeight, overflowX: 'unset' }}
      columnHovering={columnHovering}
      onMouseOverBodyCell={!isGrowthRateType ? idx => setColumnHovering(idx) : undefined}
      onClickBodyCell={
        !isGrowthRateType
          ? cell => {
              mixpanel.trackEvent('Course Grade Table Click', {
                distinct_id: myProfileId,
              });
              setSyllabusId(cell.column.id);
            }
          : undefined
      }
      cursorPointer={!isGrowthRateType}
      {...props}
    />
  ) : (
    <TKAttendanceTableStyle
      dense
      stickyHeader
      columns={tkColumns}
      data={classes}
      style={{ maxHeight, overflowX: 'unset' }}
      columnHovering={columnHovering}
      onMouseOverBodyCell={
        !isGrowthRateType && !isMastery ? idx => setColumnHovering(idx) : undefined
      }
      onClickBodyCell={
        !isGrowthRateType && !isMastery
          ? cell => {
              mixpanel.trackEvent('Course Grade Table Click', {
                distinct_id: myProfileId,
              });
              setSyllabusId(cell.column.id);
            }
          : undefined
      }
      cursorPointer={!isGrowthRateType && !isMastery}
      {...props}
    />
  );
};

CourseGradeTable.propTypes = {
  data: PropTypes.shape({ reports: PropTypes.array.isRequired, subject: PropTypes.object }),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  type: PropTypes.string,
  setSyllabusId: PropTypes.func,
  myProfileId: PropTypes.string,
  statType: PropTypes.string,
  mode: PropTypes.string,
};

export default CourseGradeTable;
